import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getTahsilatlar, Kullanici as ApiKullanici, Odeme } from '../services/authService';
import Header from '../components/Header';
import TabBar from '../components/TabBar';
import Spinner from 'react-bootstrap/Spinner';
import Alert from 'react-bootstrap/Alert';
import Card from 'react-bootstrap/Card';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import TaxiLoader from '../components/TaxiLoader';
import '../styles/Tahsilatlar.css';

interface Tahsilat extends Odeme {}

interface Kullanici {
  uyeid: string;
  uyeno: string;
  kimlikno?: string;
  grubu?: string;
  GRUBU?: string;
  durumu?: string;
  DURUMU?: string;
  ADI: string;
  SOYADI: string;
  KARTRESIM: string;
  toplam_borc?: number;
  borc_detay?: any[];
  odemeler: Tahsilat[];
  toplam_odeme?: number;
  borc?: number;
  alacak?: number;
}

const Tahsilatlar: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [kullanicilar, setKullanicilar] = useState<Kullanici[]>([]);
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [uyeId, setUyeId] = useState<string>('');

  useEffect(() => {
    const userInfoStr = localStorage.getItem('userInfo') || sessionStorage.getItem('userInfo');
    if (!userInfoStr) {
      navigate('/login');
      return;
    }

    try {
      const userInfo = JSON.parse(userInfoStr);
      if (!userInfo.uyeid) {
        navigate('/login');
        return;
      }
      setUyeId(userInfo.uyeid);
      setIsAuthenticated(true);
    } catch (error) {
      console.error('Kullanıcı bilgisi parse hatası:', error);
      navigate('/login');
    }
  }, [navigate]);

  const fetchTahsilatlar = async () => {
    if (!uyeId) {
      setError('Kullanıcı ID bulunamadı');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      const response = await getTahsilatlar(uyeId);
      console.log('Tahsilatlar servisi yanıtı:', response);

      if (response.success && response.data?.kullanicilar) {
        const formattedKullanicilar: Kullanici[] = response.data.kullanicilar.map(k => ({
          ...k,
          odemeler: k.odemeler || []
        }));
        setKullanicilar(formattedKullanicilar);
      } else {
        setError(response.message || 'Tahsilat bilgisi alınamadı');
      }
    } catch (error: any) {
      console.error('Tahsilatlar yüklenirken hata:', error);
      setError(error.message || 'Tahsilat bilgisi alınamadı');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAuthenticated && uyeId) {
      fetchTahsilatlar();
    }
  }, [isAuthenticated, uyeId]);

  const formatTarih = (tarih: string) => {
    return new Date(tarih).toLocaleDateString('tr-TR');
  };

  const formatPara = (tutar: number) => {
    return new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency: 'TRY',
      minimumFractionDigits: 2
    }).format(tutar);
  };

  const getOdemeTipi = (islem: string) => {
    switch (islem) {
      case 'KK':
        return 'Kredi Kartı';
      case 'KASA':
        return 'Nakit';
      case 'IK':
        return 'İnternet';
      default:
        return 'Diğer';
    }
  };

  return (
    <div className="tahsilatlar-container" style={{ paddingTop: 70 }}>
      <Header title="Tahsilatlar" />
      <Container className="py-4">
        {loading ? (
          <TaxiLoader message="Tahsilatlar yükleniyor..." />
        ) : error ? (
          <Alert variant="danger">{error}</Alert>
        ) : (
          <>
            {kullanicilar.map((kullanici) => (
              <Card key={kullanici.uyeid} className="mb-4">
                <Card.Header className="d-flex align-items-center">
                  <img
                    src={kullanici.KARTRESIM ? `https://crm.havalimanitaksi.com.tr/media/natural/${kullanici.KARTRESIM}` : require('../images/default-profile.png')}
                    alt="Profil"
                    className="rounded-circle me-3"
                    style={{ width: 48, height: 48, objectFit: 'cover', border: '2px solid #eee', background: '#fff' }}
                    onError={e => (e.currentTarget.src = require('../images/default-profile.png'))}
                  />
                  <div className="flex-grow-1">
                    <h5 className="mb-0">
                      {kullanici.ADI} {kullanici.SOYADI}
                      <small className="text-muted ms-2">({kullanici.uyeno})</small>
                    </h5>
                    <div className="d-flex align-items-center mt-1">
                      <span className={`badge me-2 ${((kullanici.grubu || kullanici.GRUBU) === 'UYE') ? 'bg-primary' : 'bg-secondary'}`}>{kullanici.grubu || kullanici.GRUBU}</span>
                      <span className={`badge ${((kullanici.durumu || kullanici.DURUMU) === 'AKTIF') ? 'bg-success' : 'bg-danger'}`}>{kullanici.durumu || kullanici.DURUMU}</span>
                    </div>
                  </div>
                </Card.Header>
                <Card.Body>
                  {kullanici.odemeler && kullanici.odemeler.length > 0 ? (
                    <div className="table-responsive">
                      <table className="table">
                        <thead>
                          <tr>
                            <th>Tarih</th>
                            <th>Açıklama</th>
                            <th>Tutar</th>
                            <th>Ödeme Tipi</th>
                          </tr>
                        </thead>
                        <tbody>
                          {kullanici.odemeler.map((odeme) => (
                            <tr key={odeme.COUNTER}>
                              <td>{formatTarih(odeme.TARIH)}</td>
                              <td>{odeme.ACIKLAMA}</td>
                              <td className="text-end">{formatPara(odeme.ALACAK)}</td>
                              <td>{getOdemeTipi(odeme.islem)}</td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot>
                          <tr>
                            <td colSpan={2} className="text-end fw-bold">Toplam:</td>
                            <td className="text-end fw-bold">{formatPara(kullanici.toplam_odeme || 0)}</td>
                            <td></td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  ) : (
                    <p className="text-center text-muted my-3">Henüz ödeme bulunmamaktadır.</p>
                  )}
                </Card.Body>
              </Card>
            ))}
          </>
        )}
      </Container>
      <TabBar />
    </div>
  );
};

export default Tahsilatlar; 